import React, { useState } from 'react';
import { api, EstimateDto, JobLineItemDto } from '../services/api';
import { Card, Btn, Alert, C } from './UI';
import { PaymentForm } from './PaymentForm';
import { useFeature } from '../context/FeatureFlagContext';
import { useBranding } from '../context/BrandingContext';

// TREK-78, wireframe screen 3 — customer sees the itemized quote and approves before work proceeds.

interface Props {
    jobId: string;
    estimate: EstimateDto;
    lineItems: JobLineItemDto[];
    onResolved: () => void;
}

export function EstimateApprovalModal({ jobId, estimate, lineItems, onResolved }: Props) {
    // TREK-131 follow-up: ServiceCatalogPricing jobs have no payment authorized at booking (the
    // real total isn't known until now) — approving requires authorizing the card for this
    // estimate's exact amount first, instead of just flipping the estimate's status.
    const serviceCatalogEnabled = useFeature('serviceCatalogPricing');
    const branding = useBranding();
    const contractorLabelLower = (branding.contractorNoun || 'Contractor').toLowerCase();
    const [showRequestChanges, setShowRequestChanges] = useState(false);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentIntent, setPaymentIntent] = useState<{ clientSecret: string; amount: number } | null>(null);

    const startApproval = async () => {
        setLoading(true); setError('');
        try {
            if (serviceCatalogEnabled) {
                const res = await api.createEstimatePaymentIntent(jobId);
                setPaymentIntent({ clientSecret: res.clientSecret, amount: res.amount });
            } else {
                await approve();
            }
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const approve = async () => {
        setLoading(true); setError('');
        try {
            await api.approveEstimate(jobId);
            onResolved();
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const requestChanges = async () => {
        setLoading(true); setError('');
        try {
            await api.requestEstimateChanges(jobId, note || null);
            onResolved();
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24
        }}>
            {paymentIntent ? (
                <div style={{ width: '100%', maxWidth: 420 }}>
                    <PaymentForm
                        jobId={jobId}
                        amount={paymentIntent.amount}
                        clientSecret={paymentIntent.clientSecret}
                        onSuccess={approve}
                        onCancel={() => setPaymentIntent(null)}
                    />
                </div>
            ) : (
            <Card style={{ width: '100%', maxWidth: 420 }}>
                <div style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, textAlign: 'center', marginBottom: 14 }}>
                    ⏳ Awaiting Your Approval
                </div>
                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Estimate #E-{estimate.number}</h3>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textSub }}>Review the itemized breakdown before work begins.</p>

                <Alert message={error} />

                <div style={{ background: C.bg, borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    {lineItems.map(li => (
                        <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px dashed ${C.border}`, fontSize: 13 }}>
                            <span>{li.description}{li.quantity !== 1 ? ` — ${li.quantity} ${li.pricingType === 'Hourly' ? 'hrs' : ''}`.trimEnd() : ''}</span>
                            <span style={{ fontWeight: 600 }}>${li.lineTotal.toFixed(2)}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 700 }}>Total</span>
                        <span style={{ fontSize: 20, fontWeight: 700, color: C.green }}>${estimate.totalAmount.toFixed(2)}</span>
                    </div>
                </div>

                <Btn variant="outline" fullWidth onClick={() => api.downloadEstimatePdf(jobId, estimate.number).catch(e => setError(e.message))} style={{ marginBottom: 10 }}>
                    Download PDF
                </Btn>

                {!showRequestChanges ? (
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => setShowRequestChanges(true)} style={{ flex: 1 }}>Request Changes</Btn>
                        <Btn onClick={startApproval} disabled={loading} style={{ flex: 1 }}>{loading ? 'Approving...' : 'Approve'}</Btn>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>
                                What would you like changed? (optional)
                            </label>
                            <textarea
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                rows={3}
                                placeholder="e.g. Can we skip the capacitor replacement for now?"
                                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'Inter, sans-serif', resize: 'vertical', boxSizing: 'border-box' as const }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => setShowRequestChanges(false)}>Back</Btn>
                            <Btn onClick={requestChanges} disabled={loading} fullWidth>{loading ? 'Sending...' : `Send to ${contractorLabelLower}`}</Btn>
                        </div>
                    </div>
                )}
            </Card>
            )}
        </div>
    );
}
